import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useEffect } from 'react';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2, // 2 minutes
      gcTime: 1000 * 60 * 3, // 3 minutes - reduced to free memory faster
      retry: 1,
      // Both on: a tab that comes back from the background or from a dead
      // network re-reads whatever is stale on screen. The realtime feed
      // handles the live case; these cover the gaps it cannot see.
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
  },
});

/**
 * Drops cache entries nothing is using any more, to keep memory flat on a tab
 * left open all day.
 *
 * Only ever removes queries with NO active observers. Age alone is not enough:
 * a screen driven by a WebSocket rather than by polling has a `dataUpdatedAt`
 * that legitimately stops advancing — the waiting-room display in a quiet
 * clinic is exactly that — and evicting the query out from under it left
 * `setQueryData` writing into a key that no longer existed, so live updates
 * silently stopped arriving. React Query's own `gcTime` already collects
 * unobserved entries; this is a floor under that, not a replacement for it.
 */
const cleanStaleQueries = () => {
  const now = Date.now();
  const queries = queryClient.getQueryCache().getAll();
  let removedCount = 0;

  queries.forEach((query) => {
    // A mounted component is reading this — never pull it out from under them.
    if (query.getObserversCount() > 0) return;

    const state = query.state;
    const isStale =
      state.dataUpdatedAt && now - state.dataUpdatedAt > 1000 * 60 * 5; // 5 minutes
    if (isStale) {
      queryClient.removeQueries({ queryKey: query.queryKey, exact: true });
      removedCount++;
    }
  });

  if (removedCount > 0) {
    console.log(`Cleared ${removedCount} stale queries from cache`);
  }
};

interface QueryProviderProps {
  children: ReactNode;
}

export function QueryProvider({ children }: QueryProviderProps) {
  // Periodically clear old cache entries to prevent unbounded growth
  useEffect(() => {
    const cacheCleanupInterval = setInterval(cleanStaleQueries, 1000 * 60 * 2); // Run every 2 minutes

    // Expose queryClient globally in development for memory monitoring
    if (import.meta.env.DEV && typeof window !== 'undefined') {
      // @ts-expect-error - Expose for debugging
      window.__QUERY_CLIENT__ = queryClient;
    }

    return () => {
      clearInterval(cacheCleanupInterval);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
}
